/*
 * @Author: Yumeng Xue
 * @Date: 2024-11-16 02:30:00
 * @LastEditTime: 2026-02-19 00:44:14
 * @LastEditors: Yumeng Xue
 * @Description: 
 * "Topo-Mess" TDA Messiness Analysis System Main Interface.
 * Redesigned with modern UI using Tailwind CSS + Radix UI
 * @FilePath: /topo-mess/src/App.tsx
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import papa from 'papaparse';
import './App.css';
import {
  Upload, Download, Layers, Eye, EyeOff, Settings,
  Maximize2, RotateCcw, ChevronDown, Lightbulb, Activity, Gauge,
  Database, CheckCircle2, Loader2, AlertTriangle
} from 'lucide-react';

import { LineData } from './core/binning';
import { HeatmapMode } from './core/renderer';
import { MSTResult } from './core/analysis-engine';
import { ColormapName } from './core/colormaps';
import { setColormap } from './utils/heatmap';
import Canvas, { CanvasHandle } from './components/Canvas';
import DetectiveDashboard from './components/DetectiveDashboard';
import WeatherStationMap from './components/WeatherStationMap';
import { PAPER_UI_TERMS } from './constants/paperTerminology';
import { DEMO_DATASETS, DemoDataset } from './constants/demoDatasets';
import { Cluster, generateUUID } from './types/cluster';
import { WeatherStationAsset, WeatherStationRecord } from './types/weather';

import {
  Button, Card, CardHeader, CardContent, Slider, Switch, RadioGroup, NumberInput,
  ColormapSelector, Badge
} from './components/ui';
import { TooltipProvider, Tooltip } from './components/ui/Tooltip';
import { ToastProvider, useToast } from './hooks/useToast';
import { cn } from './lib/utils';

const DEFAULT_CANVAS_WIDTH = 1000;
const DEFAULT_CANVAS_HEIGHT = 500;
const WEATHER_STATION_METADATA_PATH = 'data/weather-stations.json';
const WEATHER_QUERY_ANALYSIS_BIN_RESOLUTION = 4;
const DEFAULT_COLORMAP_BY_HEATMAP_MODE: Partial<Record<HeatmapMode, ColormapName>> = {
  Density: 'magma',
  Messiness: 'viridis',
  IlluminatedMessiness: 'magma',
};

type DataUploadOptions = {
  datasetId?: string;
  datasetName?: string;
};

interface GenericLineRow {
  lineId: string | number;
  x: string | number;
  y: string | number;
  time?: string | number;
}

interface WeatherLineRow {
  SID: string | number;
  NAME?: string;
  STATE?: string;
  WEEK: string | number;
  MAX_TEMPERATURE: string | number;
}

// Main App content wrapped with toast provider
function AppContent() {
  const toast = useToast();

  // --- 1. Core Data State ---
  const [lines, setLines] = useState<LineData[]>([]);
  const weatherStationLookupRef = useRef<Promise<Map<string, WeatherStationRecord>> | null>(null);

  // --- 2. TDA Analysis Parameters ---
  const [futureWindowW, setFutureWindowW] = useState(1000);
  const [binResolution, setBinResolution] = useState(1);

  // --- 3. Display Parameters ---
  const [heatmapMode, setHeatmapMode] = useState<HeatmapMode>("Density");
  const [lineOpacity, setLineOpacity] = useState(0.0);
  const [queryResultOpacity, setQueryResultOpacity] = useState(0.5);
  const [selectedColormap, setSelectedColormap] = useState<ColormapName>('magma');
  const [hasUserSelectedColormap, setHasUserSelectedColormap] = useState(false);

  const handleColormapChange = useCallback((colormap: ColormapName) => {
    setHasUserSelectedColormap(true);
    setColormap(colormap, true);
    setSelectedColormap(colormap);
  }, []);

  const handleHeatmapModeChange = useCallback((mode: HeatmapMode) => {
    setHeatmapMode(mode);

    if (!hasUserSelectedColormap) {
      const defaultColormap = DEFAULT_COLORMAP_BY_HEATMAP_MODE[mode];
      if (defaultColormap) {
        setColormap(defaultColormap, true);
        setSelectedColormap(defaultColormap);
      }
    }
  }, [hasUserSelectedColormap]);

  // Sync colormap changes to heatmap utility
  useEffect(() => {
    setColormap(selectedColormap, true);
  }, [selectedColormap]);

  // --- 3.5 Focus+Context Parameters ---
  const [focusContextParams, setFocusContextParams] = useState({
    threshold: 0.5,
    saturationDrop: 1.0,
    transition: 0.1,
    hatchDensity: 80.0,
    hatchThickness: 0.25,
    hatchOpacity: 0,
  });

  // --- 3.6 Histogram Equalization ---
  const [useHistogramEqualization, setUseHistogramEqualization] = useState(true);

  // --- 4. Canvas Dimensions ---
  const [canvasWidth, setCanvasWidth] = useState(DEFAULT_CANVAS_WIDTH);
  const [canvasHeight, setCanvasHeight] = useState(DEFAULT_CANVAS_HEIGHT);
  const worldSize = useRef({ width: DEFAULT_CANVAS_WIDTH, height: DEFAULT_CANVAS_HEIGHT });

  // --- 5. Canvas Ref ---
  const canvasRef = useRef<CanvasHandle>(null);

  // --- 6. Analysis Results ---
  const [analysisResults, setAnalysisResults] = useState<{
    mstResult: MSTResult | null;
    lineIds: number[];
  }>({
    mstResult: null,
    lineIds: []
  });
  const [isQueryAnalysisPending, setIsQueryAnalysisPending] = useState(false);
  const [selectedWeatherLineIds, setSelectedWeatherLineIds] = useState<number[]>([]);
  const [hoveredLineId, setHoveredLineId] = useState<number | null>(null);

  // --- 7. Line Global Scores ---
  const [lineGlobalScores, setLineGlobalScores] = useState<Float32Array | null>(null);

  // --- 8. Extracted Clusters State ---
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);

  // --- UI State ---
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [loadingDatasetId, setLoadingDatasetId] = useState<string | null>(null);
  const [activeDatasetId, setActiveDatasetId] = useState<string | null>(null);
  const [isWeatherDataset, setIsWeatherDataset] = useState(false);
  const [isDatasetPanelExpanded, setIsDatasetPanelExpanded] = useState(false);
  const activeDataset = DEMO_DATASETS.find(dataset => dataset.id === activeDatasetId);
  const queryAnalysisBinResolution = isWeatherDataset
    ? Math.max(binResolution, WEATHER_QUERY_ANALYSIS_BIN_RESOLUTION)
    : binResolution;

  // --- Handlers ---
  const handleFilterChange = useCallback((visibleLineIds: number[]) => {
    setSelectedWeatherLineIds(visibleLineIds);
    if (canvasRef.current && visibleLineIds.length >= 0) {
      canvasRef.current.setVisibleLines(visibleLineIds);
    }
  }, []);

  const handleFilterComplete = useCallback((visibleLineIds: number[]) => {
    setSelectedWeatherLineIds(visibleLineIds);
  }, []);

  const handleAnalysisComplete = useCallback((results: {
    mstResult: MSTResult | null;
    lineIds: number[];
  }) => {
    setAnalysisResults(results);
  }, []);

  const handleHoverLine = useCallback((lineId: number | null) => {
    setHoveredLineId(lineId);
    if (canvasRef.current) {
      canvasRef.current.highlightSingleLine(lineId);
    }
  }, []);

  const handleRequestCreateCluster = useCallback(async (visibleIds: number[]) => {
    if (!canvasRef.current) {
      toast.error(PAPER_UI_TERMS.status.mainViewNotReady);
      return;
    }

    try {
      const thumbnail = await canvasRef.current.renderThumbnail(visibleIds, canvasWidth, canvasHeight);

      const cluster: Cluster = {
        id: generateUUID(),
        lineIds: visibleIds,
        thumbnail,
        count: visibleIds.length,
        createdAt: Date.now()
      };

      setClusters(prev => [...prev, cluster]);

      const currentActive = new Set(canvasRef.current.getActiveLineIds());
      visibleIds.forEach(id => currentActive.delete(id));

      await canvasRef.current.updateActiveLines(Array.from(currentActive));

      toast.success(`${PAPER_UI_TERMS.subset.subset} extracted: ${visibleIds.length} lines`);
    } catch (error) {
      console.error('Failed to create cluster:', error);
      toast.error(`Failed to extract ${PAPER_UI_TERMS.subset.subset.toLowerCase()}`);
    }
  }, [canvasWidth, canvasHeight, toast]);

  const handleSelectCluster = useCallback((clusterId: string | null) => {
    setSelectedClusterId(clusterId);
  }, []);

  const handleDeleteCluster = useCallback(async (clusterId: string) => {
    const cluster = clusters.find(c => c.id === clusterId);
    if (!cluster || !canvasRef.current) return;

    try {
      const currentActive = new Set(canvasRef.current.getActiveLineIds());
      cluster.lineIds.forEach(id => currentActive.add(id));

      await canvasRef.current.updateActiveLines(Array.from(currentActive));

      setClusters(prev => prev.filter(c => c.id !== clusterId));

      if (selectedClusterId === clusterId) {
        setSelectedClusterId(null);
      }

      toast.success(`${PAPER_UI_TERMS.subset.subset} removed: ${cluster.count} lines restored`);
    } catch (error) {
      console.error('Failed to delete cluster:', error);
      toast.error(`Failed to remove ${PAPER_UI_TERMS.subset.subset.toLowerCase()}`);
    }
  }, [clusters, selectedClusterId, toast]);

  const loadWeatherStationLookup = useCallback(async (): Promise<Map<string, WeatherStationRecord>> => {
    if (!weatherStationLookupRef.current) {
      const publicUrl = (process.env.PUBLIC_URL || '.').replace(/\/$/, '');
      const metadataUrl = `${publicUrl}/${WEATHER_STATION_METADATA_PATH}`;

      weatherStationLookupRef.current = fetch(metadataUrl)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`Failed to load station metadata: ${response.status}`);
          }

          const payload = await response.json() as WeatherStationAsset;
          return new Map(payload.stations.map((station) => [station.sid, station]));
        })
        .catch((error) => {
          weatherStationLookupRef.current = null;
          throw error;
        });
    }

    return weatherStationLookupRef.current;
  }, []);

  const normalizeLinesToWorld = useCallback((parsedLines: LineData[]) => {
    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;

    parsedLines.forEach((line) => {
      line.xValues.forEach((x) => {
        xMin = Math.min(xMin, x);
        xMax = Math.max(xMax, x);
      });
      line.yValues.forEach((y) => {
        yMin = Math.min(yMin, y);
        yMax = Math.max(yMax, y);
      });
    });

    const { width, height } = worldSize.current;
    const xSpan = xMax - xMin || 1;
    const ySpan = yMax - yMin || 1;

    return parsedLines.map((line) => ({
      ...line,
      xValues: line.xValues.map((x) => ((x - xMin) / xSpan) * width),
      yValues: line.yValues.map((y) => ((y - yMin) / ySpan) * height),
    }));
  }, []);

  const parseGenericLineData = useCallback((data: GenericLineRow[]): LineData[] => {
    const groupBy = <T extends Record<string, any>>(xs: T[], key: keyof T) => {
      return xs.reduce(function (rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
      }, {} as Record<string, T[]>);
    };

    const groupedData = groupBy(data, 'lineId');
    const parsedLines: LineData[] = [];
    const is2D = data[0].time !== undefined;

    for (const rawLine of Object.values(groupedData)) {
      if (rawLine.length <= 1) continue;

      if (is2D) {
        rawLine.sort((a, b) => Number(a.time ?? 0) - Number(b.time ?? 0));
      } else {
        rawLine.sort((a, b) => Number(a.x) - Number(b.x));
      }

      const line: LineData = { xValues: [], yValues: [] };
      if (is2D) line.times = [];

      for (let i = 0; i < rawLine.length; ++i) {
        const x = Number(rawLine[i].x);
        const y = Number(rawLine[i].y);

        if (Number.isNaN(x) || Number.isNaN(y)) continue;

        line.xValues.push(x);
        line.yValues.push(y);
        if (is2D && line.times) line.times.push(Number(rawLine[i].time ?? 0));
      }

      if (line.xValues.length > 1) {
        parsedLines.push(line);
      }
    }

    return parsedLines;
  }, []);

  const parseWeatherLineData = useCallback(async (data: WeatherLineRow[]): Promise<LineData[]> => {
    const stationLookup = await loadWeatherStationLookup();
    const groupedData = data.reduce<Record<string, WeatherLineRow[]>>((acc, row) => {
      const sid = String(row.SID ?? '').trim();
      if (!sid) {
        return acc;
      }

      (acc[sid] = acc[sid] || []).push(row);
      return acc;
    }, {});

    const parsedLines: LineData[] = [];

    for (const [sid, rawLine] of Object.entries(groupedData) as [string, WeatherLineRow[]][]) {
      if (rawLine.length <= 1) continue;

      rawLine.sort((a, b) => Number(a.WEEK) - Number(b.WEEK));

      const station = stationLookup.get(sid);
      const firstRow = rawLine[0];
      const line: LineData = {
        sid,
        label: String(station?.name ?? firstRow.NAME ?? sid),
        state: String(station?.state ?? firstRow.STATE ?? ''),
        latitude: station?.latitude ?? null,
        longitude: station?.longitude ?? null,
        elevation: station?.elevation ?? null,
        xValues: [],
        yValues: [],
      };

      rawLine.forEach((row) => {
        const x = Number(row.WEEK);
        const y = Number(row.MAX_TEMPERATURE);
        if (Number.isNaN(x) || Number.isNaN(y)) {
          return;
        }

        line.xValues.push(x);
        line.yValues.push(y);
      });

      if (line.xValues.length > 1) {
        parsedLines.push(line);
      }
    }

    return parsedLines;
  }, [loadWeatherStationLookup]);

  const handleDataUpload = useCallback((file: File, options: DataUploadOptions = {}) => {
    return new Promise<void>((resolve) => {
      papa.parse<any, File>(file, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: async (results: papa.ParseResult<any>) => {
          try {
            const data = results.data;
            if (data.length === 0) throw new Error("No data found in CSV.");

            const firstRow = data[0];
            const detectedWeatherDataset =
              firstRow.SID !== undefined &&
              firstRow.WEEK !== undefined &&
              firstRow.MAX_TEMPERATURE !== undefined;

            const parsedLines = detectedWeatherDataset
              ? await parseWeatherLineData(data)
              : parseGenericLineData(data);
            if (parsedLines.length === 0) throw new Error("No valid lines parsed.");

            const normalizedLines = normalizeLinesToWorld(parsedLines);

            const sourceLabel = options.datasetName ? ` from ${options.datasetName}` : '';
            toast.success(
              detectedWeatherDataset
                ? `Loaded ${normalizedLines.length} weather station lines${sourceLabel}`
                : `Loaded ${normalizedLines.length} lines${sourceLabel}`
            );
            setHoveredLineId(null);
            setSelectedWeatherLineIds([]);
            setLines(normalizedLines);
            setAnalysisResults({ mstResult: null, lineIds: [] });
            setIsQueryAnalysisPending(false);
            setLineGlobalScores(null);
            setClusters([]);
            setSelectedClusterId(null);
            setIsWeatherDataset(detectedWeatherDataset);
            setActiveDatasetId(options.datasetId ?? null);
            if (options.datasetId) {
              setIsDatasetPanelExpanded(false);
            }
          } catch (error) {
            console.error("Data loading error:", error);
            toast.error((error as Error).message);
          } finally {
            resolve();
          }
        },
        error: (error: Error) => {
          console.error("Data loading error:", error);
          toast.error(error.message);
          resolve();
        }
      });
    });
  }, [normalizeLinesToWorld, parseGenericLineData, parseWeatherLineData, toast]);

  const handleBuiltInDatasetLoad = useCallback(async (dataset: DemoDataset) => {
    setLoadingDatasetId(dataset.id);
    try {
      const publicUrl = (process.env.PUBLIC_URL || '.').replace(/\/$/, '');
      const datasetUrl = `${publicUrl}/${dataset.publicPath}`;
      const response = await fetch(datasetUrl);

      if (!response.ok) {
        throw new Error(`Failed to load ${dataset.name} (${response.status})`);
      }

      const nextWorldSize = {
        width: dataset.resolution.width,
        height: dataset.resolution.height,
      };
      worldSize.current = nextWorldSize;
      setCanvasWidth(nextWorldSize.width);
      setCanvasHeight(nextWorldSize.height);

      const blob = await response.blob();
      const file = new File([blob], dataset.fileName, { type: 'text/csv' });
      await handleDataUpload(file, {
        datasetId: dataset.id,
        datasetName: dataset.name,
      });
    } catch (error) {
      console.error("Built-in dataset loading error:", error);
      toast.error((error as Error).message);
    } finally {
      setLoadingDatasetId(null);
    }
  }, [handleDataUpload, toast]);

  const applyResolution = useCallback(() => {
    worldSize.current = { width: canvasWidth, height: canvasHeight };

    if (lines.length > 0) {
      toast.info(PAPER_UI_TERMS.status.reloadingNeeded);
    } else {
      toast.success(`Figure resolution set to ${canvasWidth} × ${canvasHeight}`);
    }
  }, [canvasWidth, canvasHeight, lines.length, toast]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void handleDataUpload(file);
    }
  };

  const heatmapOptions = [
    // { value: 'Saliency', label: 'Saliency', icon: <Activity className="w-3 h-3" /> },
    { value: 'Density', label: PAPER_UI_TERMS.controls.densityPlot, icon: <Layers className="w-3 h-3" /> },
    { value: 'Messiness', label: PAPER_UI_TERMS.controls.structuralInconsistencyField, icon: <Gauge className="w-3 h-3" /> },
    { value: 'IlluminatedMessiness', label: PAPER_UI_TERMS.controls.focusContextView, icon: <Lightbulb className="w-3 h-3" /> },
    { value: 'Off', label: PAPER_UI_TERMS.controls.linesOnly, icon: <EyeOff className="w-3 h-3" /> },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-50 via-white to-surface-100">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-surface-200 shadow-glass-sm">
        <div className="flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-600 flex items-center justify-center shadow-glow">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold bg-gradient-to-r from-surface-800 to-surface-600 bg-clip-text text-transparent">
                {PAPER_UI_TERMS.header.title}
              </h1>
              <p className="text-xs text-surface-500">{PAPER_UI_TERMS.header.subtitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {lines.length > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-50 text-primary-700">
                <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
                <span className="text-sm font-medium">
                  {activeDataset ? `${activeDataset.name}: ` : ''}{lines.length} lines in ensemble
                </span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Sidebar - Controls */}
        <aside className="w-80 flex-shrink-0 bg-white border-r border-surface-200 overflow-y-auto">
          <div className="p-4 space-y-4">

            {/* Built-in Demo Datasets */}
            <Card variant="gradient" className="border-primary-300 shadow-glow rounded-lg">
              <button
                type="button"
                onClick={() => setIsDatasetPanelExpanded((expanded) => !expanded)}
                className="w-full text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-primary-700">
                      <Database className="w-4 h-4" />
                      <h2 className="text-sm font-bold uppercase tracking-wide">Demo datasets</h2>
                    </div>
                    <p className="mt-1 text-xs leading-5 text-surface-600">
                      {activeDataset
                        ? `Current: ${activeDataset.name}`
                        : 'Seven real datasets from the paper and supplementary material.'}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <Badge variant={activeDataset ? 'success' : 'primary'}>{activeDataset ? 'Loaded' : '7 real'}</Badge>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 text-primary-600 transition-transform duration-200',
                        isDatasetPanelExpanded && 'rotate-180'
                      )}
                    />
                  </div>
                </div>
              </button>

              <div className="mt-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  This system requires WebGPU. If your browser or device does not support it, the main view may not render correctly.
                  Datasets download in the browser, so large files may take a little while to load.
                </span>
              </div>

              {activeDataset && !isDatasetPanelExpanded && (
                <button
                  type="button"
                  onClick={() => setIsDatasetPanelExpanded(true)}
                  className="mt-3 w-full rounded-lg border border-primary-200 bg-white px-3 py-2 text-left text-xs text-surface-700 transition-colors hover:bg-primary-50"
                >
                  <span className="font-semibold text-primary-700">Change dataset</span>
                  <span className="ml-2 text-surface-500">
                    {activeDataset.lineCount} · {activeDataset.resolution.width}×{activeDataset.resolution.height}
                  </span>
                </button>
              )}

              {isDatasetPanelExpanded && (
                <div className="mt-3 space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
                  {DEMO_DATASETS.map((dataset) => {
                    const isActive = activeDatasetId === dataset.id;
                    const isLoading = loadingDatasetId === dataset.id;

                    return (
                      <button
                        key={dataset.id}
                        type="button"
                        onClick={() => void handleBuiltInDatasetLoad(dataset)}
                        disabled={loadingDatasetId !== null}
                        className={cn(
                          'w-full rounded-lg border p-2.5 text-left transition-all duration-200',
                          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                          isActive
                            ? 'border-primary-500 bg-primary-50 text-primary-900'
                            : 'border-surface-200 bg-white text-surface-800 hover:border-primary-300 hover:bg-primary-50',
                          loadingDatasetId && !isLoading && 'opacity-60'
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold leading-5 break-words">
                              {dataset.name}
                            </div>
                            <p className="mt-0.5 max-h-10 overflow-hidden text-xs leading-5 text-surface-600 break-words">
                              {dataset.description}
                            </p>
                          </div>
                          <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center text-primary-600">
                            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                            {!isLoading && isActive && <CheckCircle2 className="h-4 w-4" />}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <Badge variant={isActive ? 'primary' : 'default'}>{dataset.category}</Badge>
                          <Badge variant="default">{dataset.lineCount}</Badge>
                          <Badge variant="default">{dataset.sampleCount}</Badge>
                          <Badge variant="default">{dataset.resolution.width}×{dataset.resolution.height}</Badge>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Analysis Parameters */}
            <Card>
              <CardHeader icon={<Settings className="w-4 h-4" />}>{PAPER_UI_TERMS.panels.fieldParameters}</CardHeader>
              <CardContent>
                <Slider
                  label={PAPER_UI_TERMS.controls.pathExtent}
                  value={[futureWindowW]}
                  onValueChange={(v) => setFutureWindowW(v[0])}
                  min={1}
                  max={2000}
                  step={2}
                />

                <NumberInput
                  label={PAPER_UI_TERMS.controls.gridResolution}
                  value={binResolution}
                  onChange={setBinResolution}
                  min={1}
                  max={50}
                  suffix="px"
                />
              </CardContent>
            </Card>


            {/* Display Controls */}
            <Card>
              <CardHeader icon={<Eye className="w-4 h-4" />}>{PAPER_UI_TERMS.panels.viewSettings}</CardHeader>
              <CardContent>
                <RadioGroup
                  label={PAPER_UI_TERMS.controls.view}
                  options={heatmapOptions}
                  value={heatmapMode}
                  onValueChange={(v) => handleHeatmapModeChange(v as HeatmapMode)}
                  size="sm"
                />

                {/* Colormap Selection */}
                <ColormapSelector
                  value={selectedColormap}
                  onChange={handleColormapChange}
                  label={PAPER_UI_TERMS.controls.colormap}
                />

                <Switch
                  label={PAPER_UI_TERMS.controls.histogramEqualization}
                  description="Enhances contrast in visualizations"
                  checked={useHistogramEqualization}
                  onCheckedChange={setUseHistogramEqualization}
                />

                <Slider
                  label={PAPER_UI_TERMS.controls.lineOpacity}
                  value={[lineOpacity]}
                  onValueChange={(v) => setLineOpacity(v[0])}
                  min={0}
                  max={0.5}
                  step={0.005}
                  formatValue={(v) => `${(v * 100).toFixed(1)}%`}
                />
              </CardContent>
            </Card>

            {/* Focus+Context Controls - Only when IlluminatedMessiness mode */}
            {heatmapMode === "IlluminatedMessiness" && (
              <Card variant="glass" className="animate-fade-in">
                <CardHeader icon={<Lightbulb className="w-4 h-4" />}>{PAPER_UI_TERMS.panels.focusContextViewSettings}</CardHeader>
                <CardContent>
                  <Slider
                    label={PAPER_UI_TERMS.controls.inconsistencyThreshold}
                    value={[focusContextParams.threshold]}
                    onValueChange={(v) => setFocusContextParams(prev => ({
                      ...prev,
                      threshold: v[0]
                    }))}
                    min={0}
                    max={1}
                    step={0.01}
                    formatValue={(v) => v.toFixed(2)}
                  />

                  <Slider
                    label={PAPER_UI_TERMS.controls.transitionWidth}
                    value={[focusContextParams.transition]}
                    onValueChange={(v) => setFocusContextParams(prev => ({
                      ...prev,
                      transition: v[0]
                    }))}
                    min={0.01}
                    max={0.5}
                    step={0.01}
                    formatValue={(v) => v.toFixed(2)}
                  />

                  <Slider
                    label={PAPER_UI_TERMS.controls.contextDesaturation}
                    value={[focusContextParams.saturationDrop]}
                    onValueChange={(v) => setFocusContextParams(prev => ({
                      ...prev,
                      saturationDrop: v[0]
                    }))}
                    min={0}
                    max={1}
                    step={0.01}
                    formatValue={(v) => `${(v * 100).toFixed(0)}%`}
                  />

                  {/* Hatch controls hidden — currently disabled (hatchOpacity=0)
                  <Slider
                    label="Hatch Density"
                    value={[focusContextParams.hatchDensity]}
                    onValueChange={(v) => setFocusContextParams(prev => ({
                      ...prev,
                      hatchDensity: v[0]
                    }))}
                    min={20}
                    max={200}
                    step={5}
                    formatValue={(v) => v.toFixed(0)}
                  />

                  <Slider
                    label="Hatch Thickness"
                    value={[focusContextParams.hatchThickness]}
                    onValueChange={(v) => setFocusContextParams(prev => ({
                      ...prev,
                      hatchThickness: v[0]
                    }))}
                    min={0.05}
                    max={0.8}
                    step={0.05}
                    formatValue={(v) => v.toFixed(2)}
                  />

                  <Slider
                    label="Hatch Opacity"
                    value={[focusContextParams.hatchOpacity]}
                    onValueChange={(v) => setFocusContextParams(prev => ({
                      ...prev,
                      hatchOpacity: v[0]
                    }))}
                    min={0}
                    max={1}
                    step={0.01}
                    formatValue={(v) => `${(v * 100).toFixed(0)}%`}
                  />
                  */}

                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<RotateCcw className="w-3 h-3" />}
                    onClick={() => setFocusContextParams({
                      threshold: 0.5,
                      saturationDrop: 1.0,
                      transition: 0.1,
                      hatchDensity: 80.0,
                      hatchThickness: 0.25,
                      hatchOpacity: 0,
                    })}
                    className="w-full"
                  >
                    {PAPER_UI_TERMS.controls.resetFieldSettings}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Canvas Resolution - Collapsible */}
            <Card>
              <button
                onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
                className="w-full flex items-center justify-between"
              >
                <CardHeader icon={<Maximize2 className="w-4 h-4" />}>{PAPER_UI_TERMS.panels.figureResolution}</CardHeader>
                <ChevronDown
                  className={cn(
                    'w-5 h-5 text-surface-400 transition-transform duration-200',
                    isSettingsExpanded && 'rotate-180'
                  )}
                />
              </button>

              {isSettingsExpanded && (
                <CardContent className="mt-4 animate-slide-down">
                  <div className="grid grid-cols-2 gap-3">
                    <NumberInput
                      label="Width"
                      value={canvasWidth}
                      onChange={setCanvasWidth}
                      min={1}
                      max={4000}
                      step={1}
                      suffix="px"
                    />
                    <NumberInput
                      label="Height"
                      value={canvasHeight}
                      onChange={setCanvasHeight}
                      min={1}
                      max={4000}
                      step={1}
                      suffix="px"
                    />
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={applyResolution}
                    className="w-full mt-3"
                  >
                    {PAPER_UI_TERMS.controls.applyFigureResolution}
                  </Button>
                </CardContent>
              )}
            </Card>

            {/* Data Upload */}
            <Card variant="gradient">
              <CardHeader icon={<Upload className="w-4 h-4" />}>{PAPER_UI_TERMS.panels.lineEnsembleInput}</CardHeader>
              <CardContent>
                <label className="block w-full">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <div className="flex items-center justify-center gap-2 w-full h-12 rounded-lg border-2 border-dashed border-primary-300 bg-primary-50 hover:bg-primary-100 hover:border-primary-400 text-primary-600 font-medium cursor-pointer transition-all duration-200">
                    <Upload className="w-4 h-4" />
                    {PAPER_UI_TERMS.controls.loadLineEnsemble}
                  </div>
                </label>
              </CardContent>
            </Card>

            {/* Export */}
            <Card>
              <CardHeader icon={<Download className="w-4 h-4" />}>{PAPER_UI_TERMS.panels.figureExport}</CardHeader>
              <CardContent>
                <Tooltip content="Save the main view as a PNG figure">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Download className="w-4 h-4" />}
                    onClick={() => {
                      if (canvasRef.current) {
                        canvasRef.current.downloadCanvas();
                      } else {
                        toast.error(PAPER_UI_TERMS.status.mainViewNotReady);
                      }
                    }}
                    className="w-full"
                    disabled={lines.length === 0}
                  >
                    {PAPER_UI_TERMS.controls.saveMainView}
                  </Button>
                </Tooltip>
              </CardContent>
            </Card>
          </div>
        </aside>

        {/* Main Canvas Area */}
        <main className="flex-1 overflow-hidden bg-gradient-to-br from-surface-50 to-surface-100 p-6">
          <div className="h-full rounded-2xl bg-white shadow-glass border border-surface-200 overflow-hidden">
            <Canvas
              ref={canvasRef}
              lines={lines}
              futureWindowW={futureWindowW}
              binResolution={binResolution}
              heatmapMode={heatmapMode}
              lineOpacity={lineOpacity}
              queryResultOpacity={queryResultOpacity}
              queryAnalysisBinResolution={queryAnalysisBinResolution}
              width={canvasWidth}
              height={canvasHeight}
              selectedClusterId={selectedClusterId}
              clusters={clusters}
              onAnalysisComplete={handleAnalysisComplete}
              onQueryAnalysisStateChange={setIsQueryAnalysisPending}
              onLineFinalScoresUpdate={setLineGlobalScores}
              focusContextParams={focusContextParams}
              useHistogramEqualization={useHistogramEqualization}
              selectedColormap={selectedColormap}
            />
          </div>
        </main>

        {/* Right Sidebar - Weather Map + Detective Dashboard */}
        <aside className="w-[420px] flex flex-shrink-0 flex-col bg-white border-l border-surface-200 overflow-hidden">
          {isWeatherDataset && lines.length > 0 && (
            <div className="flex-shrink-0 border-b border-surface-200 bg-white">
              <WeatherStationMap
                lines={lines}
                selectedLineIds={selectedWeatherLineIds}
                hoveredLineId={hoveredLineId}
                onHoverLine={handleHoverLine}
                height={300}
              />
            </div>
          )}

          <div className="min-h-0 flex-1">
            <DetectiveDashboard
              lineFinalScores={lineGlobalScores}
              lineIds={analysisResults.lineIds}
              isQueryAnalysisPending={isQueryAnalysisPending}
              onFilterChange={handleFilterChange}
              onFilterComplete={handleFilterComplete}
              queryResultOpacity={queryResultOpacity}
              onQueryResultOpacityChange={setQueryResultOpacity}
              onHoverLine={handleHoverLine}
              onRequestCreateCluster={handleRequestCreateCluster}
              clusters={clusters}
              onSelectCluster={handleSelectCluster}
              onDeleteCluster={handleDeleteCluster}
              width={420}
              height={window.innerHeight - 64}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}

function App() {
  return (
    <TooltipProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </TooltipProvider>
  );
}

export default App;
